import React from "react";
import { Link } from "react-router-dom";
import { useDrag } from "react-dnd";
import { useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { Country } from "@syos-music/common";
import { MaterialColor } from "@syos-music/instruments";

import { Box, Paper, Typography, Stack } from "@mui/material";
import {
  AlertDecagram as UrgentIcon,
  Printer3dNozzle as PrintedIcon,
} from "mdi-material-ui";

import MouthpieceLabel from "components/mouthpiece/MouthpieceLabel";

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const WEEK = DAY * 7;
const MONTH = DAY * 30;
const YEAR = DAY * 365;

function timeAgo(intl, then) {
  const now = Date.now();
  then = new Date(then).getTime();

  const seconds = Math.round((then - now) / 1000);
  const diff = Math.abs(seconds);

  const [value, unit] =
    diff < MINUTE
      ? [Math.round(seconds), "second"]
      : diff < HOUR
      ? [Math.round(seconds / MINUTE), "minute"]
      : diff < DAY
      ? [Math.round(seconds / HOUR), "hour"]
      : diff < WEEK
      ? [Math.round(seconds / DAY), "day"]
      : diff < MONTH
      ? [Math.round(seconds / WEEK), "week"]
      : diff < YEAR
      ? [Math.round(seconds / MONTH), "month"]
      : [Math.round(seconds / YEAR), "year"];

  return intl.formatRelativeTime(value, unit);
}

export default function OrdersPageOrderListItem({ order }) {
  const intl = useIntl();

  const [, drag] = useDrag({
    type: "order",
    item: {
      id: order.id,
      order,
    },
  });

  const topSoundShapings = order.cart
    .reduce(
      (acc, cartItem) => [
        ...acc,
        cartItem.soundShapings[cartItem.soundShapings.length - 1],
      ],
      []
    )
    .filter(Boolean);

  const date = intl.formatDate(order.lastStateChange, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const stateChangeAgo = timeAgo(intl, order.lastStateChange);
  const title = [stateChangeAgo, `Le ${date}`].join("\n");

  const deadlineDiff = order.deadline ? timeAgo(intl, order.deadline) : "";
  const deadlineTitle =
    deadlineDiff &&
    ["Deadline :", deadlineDiff, `le ${intl.formatDate(order.deadline)}`].join(
      "\n"
    );

  return (
    <Paper
      ref={drag}
      component={Link}
      to={`/orders/${order.id}`}
      title={title}
      sx={{ textDecoration: "none", display: "block", p: 1 }}
    >
      <Stack spacing={1} alignItems="center" direction="row">
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <Typography noWrap>
            {order.label ?? order.user?.profile.fullName}
            {!!order.label && (
              <Typography component="span" color="textSecondary">
                &nbsp;({order.user?.profile.fullName})
              </Typography>
            )}
          </Typography>

          <Stack direction="row">
            <Typography noWrap color="textSecondary" sx={{ flexGrow: 1 }}>
              <Country
                withEmoji
                countryCode={order.shippingAddress?.countryId}
              />
            </Typography>

            <Typography noWrap color="textSecondary" align="right">
              {order.shippingMethod?.label.fr}
            </Typography>
          </Stack>
        </Box>

        {order.deadline && (
          <span title={deadlineTitle}>
            <UrgentIcon
              color={
                new Date(order.deadline) < Date.now() ? "error" : "disabled"
              }
            />
          </span>
        )}
      </Stack>

      {topSoundShapings.map((soundShaping) => (
        <Typography key={soundShaping.id} component="div" noWrap>
          <small>
            {soundShaping.isPrinted && (
              <PrintedIcon
                style={{
                  fontSize: "1.1em",
                  transform: "translateY(15%)",
                  marginRight: "0.5ch",
                }}
              />
            )}

            {soundShaping.color && (
              <MaterialColor
                color={soundShaping.color}
                variant="preview"
                inline
                sx={{ mr: "1ch" }}
              />
            )}

            <MouthpieceLabel mouthpieceRef={soundShaping.mouthpieceRef} />
          </small>
        </Typography>
      ))}
    </Paper>
  );
}

OrdersPageOrderListItem.fragments = {
  order: gql`
    fragment Orders_OrdersPageOrderListItem on Order {
      id
      lastStateChange
      deadline
      label

      state {
        id
      }

      user {
        id
        profile {
          fullName
        }
      }

      shippingAddress {
        countryId
      }

      shippingMethod {
        id
        label {
          fr
        }
      }

      cart {
        soundShapings {
          id
          color
          isPrinted

          mouthpieceRef {
            id

            ...MouthpieceLabel_mouthpieceRef
          }
        }
      }
    }

    ${MouthpieceLabel.fragments.mouthpieceRef}
  `,
};
